import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Download, Captions, Mic, Scissors, Combine, Sparkles, Loader2, Wand2 } from 'lucide-react';
import { EditorCaptionsPanel } from './EditorCaptionsPanel';

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const toClock = (seconds, dotSep) => {
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}${dotSep ? '.' : ','}${String(ms).padStart(3, '0')}`;
};

const parseClock = (str) => {
  const m = str.trim().match(/(\d+):(\d{2}):(\d{2})[.,](\d{3})/);
  if (!m) return 0;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4]) / 1000;
};

/** Parses REAL SRT/VTT text into item-relative (0 = this caption item's own
 * start) segments. No fabricated timing — whatever the file says is what
 * gets imported. */
function parseSubtitleText(raw, isVtt) {
  const body = isVtt ? raw.replace(/^WEBVTT.*?\n/s, '') : raw;
  const blocks = body.split(/\r?\n\r?\n+/).map((b) => b.trim()).filter(Boolean);
  const segments = [];
  blocks.forEach((block) => {
    const lines = block.split(/\r?\n/);
    const timeLineIdx = lines.findIndex((l) => l.includes('-->'));
    if (timeLineIdx === -1) return;
    const [startStr, endStr] = lines[timeLineIdx].split('-->');
    const start = parseClock(startStr);
    const end = parseClock(endStr);
    const text = lines.slice(timeLineIdx + 1).filter((l) => !/^\d+$/.test(l.trim())).join(' ').trim();
    if (text && end > start) segments.push({ id: newId(), text, start_seconds: start, end_seconds: end, words: [] });
  });
  return segments.sort((a, b) => a.start_seconds - b.start_seconds);
}

function buildSubtitleText(segments, isVtt) {
  const sorted = [...segments].sort((a, b) => a.start_seconds - b.start_seconds);
  const lines = isVtt ? ['WEBVTT', ''] : [];
  sorted.forEach((seg, i) => {
    if (!isVtt) lines.push(String(i + 1));
    lines.push(`${toClock(seg.start_seconds, isVtt)} --> ${toClock(seg.end_seconds, isVtt)}`);
    lines.push(seg.text || '');
    lines.push('');
  });
  return lines.join('\n');
}

function downloadText(filename, content) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const CAPTION_ANIM_STYLES = ['none', 'karaoke_highlight', 'pop_word'];

/** Finds the Master video item whose source this caption block should be
 * transcribed from: prefers one actually overlapping this caption item's
 * own timeline position, falling back to the first item using that source. */
function findLinkedVideoItem(masterComposition, captionItem, linkedSourceId) {
  if (!masterComposition || !linkedSourceId) return null;
  const candidates = masterComposition.timeline_items.filter((i) => i.item_type === 'video' && i.content_source_id === linkedSourceId);
  if (candidates.length === 0) return null;
  const overlapping = candidates.find((i) =>
    captionItem.timeline_start_seconds < i.timeline_start_seconds + i.duration_seconds &&
    captionItem.timeline_start_seconds + captionItem.duration_seconds > i.timeline_start_seconds
  );
  return overlapping || candidates[0];
}

/** Converts SOURCE-relative transcript segments/words into the item-
 * relative basis captions use, accounting for the linked video item's own
 * trim (source_in/out) and playback speed — segments entirely outside the
 * trimmed range are dropped, partially-overlapping ones are clipped. */
function retimeTranscriptToItem(transcriptSegments, videoItem) {
  const speed = (videoItem.speed_percent ?? 100) / 100 || 1;
  const inS = videoItem.source_in_seconds ?? 0;
  const outS = videoItem.source_out_seconds ?? Infinity;
  const out = [];
  (transcriptSegments || []).forEach((seg) => {
    if (seg.end_seconds <= inS || seg.start_seconds >= outS) return;
    const clampedStart = Math.max(seg.start_seconds, inS);
    const clampedEnd = Math.min(seg.end_seconds, outS);
    const words = (seg.words || [])
      .filter((w) => w.end_seconds > inS && w.start_seconds < outS)
      .map((w) => ({
        text: w.text,
        start_seconds: (Math.max(w.start_seconds, inS) - inS) / speed,
        end_seconds: (Math.min(w.end_seconds, outS) - inS) / speed,
      }));
    out.push({
      id: seg.id || newId(), text: seg.text,
      start_seconds: (clampedStart - inS) / speed,
      end_seconds: (clampedEnd - inS) / speed,
      words,
    });
  });
  return out;
}

/**
 * Captions for the Main Editor's text track. Reuses the SAME segment-list
 * editor as the Clip Editor (EditorCaptionsPanel) — one shared editing
 * engine — and adds SRT/VTT import/export, manual even-timing word
 * auto-split for highlighting, segment split/merge, and REAL automatic
 * captions: picking which source's speech to transcribe, requesting (or
 * reusing a cached) transcript from the backend orchestration layer, and
 * — once a transcript is actually `completed` — retiming its SOURCE-
 * relative segments to this item's own clock before applying them. No
 * transcription provider is connected anywhere in this codebase today, so
 * every request honestly resolves to "pending integration"; it never
 * fabricates segments or claims a real result.
 */
export function EditorCaptionControls({ item, localTime, siblingSources, masterComposition, onRequestTranscription, onSegmentsChange, onTextChange }) {
  const fileInputRef = useRef(null);
  const [requesting, setRequesting] = useState(false);
  const [lastTranscript, setLastTranscript] = useState(null);
  const text = item.text || {};
  const segments = text.segments || [];
  const transcription = text.transcription || { status: 'not_requested' };
  const linkedSourceId = text.linked_source_id || siblingSources?.find((s) => s.duration_seconds)?.id || '';

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVtt = file.name.toLowerCase().endsWith('.vtt');
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseSubtitleText(String(reader.result), isVtt);
      onSegmentsChange(parsed);
      onTextChange({ subtitle_source_format: isVtt ? 'vtt' : 'srt' });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const exportAs = (isVtt) => {
    if (segments.length === 0) return;
    downloadText(isVtt ? 'captions.vtt' : 'captions.srt', buildSubtitleText(segments, isVtt));
  };

  const requestTranscription = async () => {
    if (!linkedSourceId || !onRequestTranscription) return;
    setRequesting(true);
    try {
      const transcript = await onRequestTranscription(linkedSourceId);
      setLastTranscript(transcript);
      onTextChange({
        linked_source_id: linkedSourceId,
        transcription: { status: transcript.status, language: transcript.language, provider: transcript.provider, error: transcript.error },
      });
    } finally {
      setRequesting(false);
    }
  };

  const applyTranscript = () => {
    if (!lastTranscript || lastTranscript.status !== 'completed') return;
    const videoItem = findLinkedVideoItem(masterComposition, item, linkedSourceId);
    if (!videoItem) return;
    onSegmentsChange(retimeTranscriptToItem(lastTranscript.segments, videoItem));
  };

  const autoSplitWords = (segId) => {
    const seg = segments.find((s) => s.id === segId);
    if (!seg || !seg.text.trim()) return;
    const words = seg.text.trim().split(/\s+/);
    const span = Math.max(0.01, seg.end_seconds - seg.start_seconds);
    const per = span / words.length;
    // Item-relative (same clock as the segment itself), NOT segment-relative —
    // matches MasterCaptionWord's documented timing basis.
    const built = words.map((w, i) => ({ text: w, start_seconds: seg.start_seconds + i * per, end_seconds: seg.start_seconds + (i + 1) * per }));
    onSegmentsChange(segments.map((s) => (s.id === segId ? { ...s, words: built } : s)));
  };

  const splitSegment = (segId) => {
    const seg = segments.find((s) => s.id === segId);
    if (!seg) return;
    const mid = (seg.start_seconds + seg.end_seconds) / 2;
    const words = seg.text.trim().split(/\s+/);
    const half = Math.max(1, Math.ceil(words.length / 2));
    const first = { ...seg, end_seconds: mid, text: words.slice(0, half).join(' '), words: [] };
    const second = { id: newId(), start_seconds: mid, end_seconds: seg.end_seconds, text: words.slice(half).join(' '), words: [] };
    onSegmentsChange(segments.flatMap((s) => (s.id === segId ? [first, second] : [s])));
  };

  const mergeWithNext = (segId) => {
    const sorted = [...segments].sort((a, b) => a.start_seconds - b.start_seconds);
    const idx = sorted.findIndex((s) => s.id === segId);
    const next = sorted[idx + 1];
    if (!next) return;
    const merged = { ...sorted[idx], end_seconds: next.end_seconds, text: `${sorted[idx].text} ${next.text}`.trim(), words: [] };
    onSegmentsChange(sorted.filter((s) => s.id !== segId && s.id !== next.id).concat(merged));
  };

  return (
    <div className="space-y-3 pt-2 border-t border-white/5" data-testid="caption-controls-panel">
      <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Captions className="h-3 w-3" />Captions</p>

      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileInputRef} type="file" accept=".srt,.vtt" className="hidden" onChange={handleImport} data-testid="caption-import-input" />
        <Button size="sm" variant="outline" className="h-7 text-[10px] border-white/10" onClick={() => fileInputRef.current?.click()} data-testid="caption-import-button"><Upload className="h-3 w-3 mr-1" />Import SRT/VTT</Button>
        <Button size="sm" variant="outline" className="h-7 text-[10px] border-white/10" disabled={segments.length === 0} onClick={() => exportAs(false)} data-testid="caption-export-srt-button"><Download className="h-3 w-3 mr-1" />SRT</Button>
        <Button size="sm" variant="outline" className="h-7 text-[10px] border-white/10" disabled={segments.length === 0} onClick={() => exportAs(true)} data-testid="caption-export-vtt-button"><Download className="h-3 w-3 mr-1" />VTT</Button>
      </div>

      <div className="space-y-1.5 p-2 rounded-lg border border-white/5 bg-white/[0.02]">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-[#8A93A6] flex items-center gap-1.5"><Mic className="h-3 w-3" />Automatic Captions</span>
          {transcription.status === 'not_requested' || transcription.status === 'failed' ? (
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" disabled={!linkedSourceId || requesting} onClick={requestTranscription} data-testid="caption-request-stt-button">
              {requesting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Generate
            </Button>
          ) : transcription.status === 'completed' ? (
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={applyTranscript} data-testid="caption-apply-transcript-button">
              <Wand2 className="h-3 w-3 mr-1" />Apply Transcript
            </Button>
          ) : (
            <span className="text-[10px] text-amber-400/80" data-testid="caption-stt-status">
              {transcription.status === 'pending_integration' ? 'Pending integration' : transcription.status}
            </span>
          )}
        </div>
        {siblingSources?.length > 0 && (
          <Select value={linkedSourceId} onValueChange={(v) => onTextChange({ linked_source_id: v })}>
            <SelectTrigger className="h-7 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid="caption-linked-source-select"><SelectValue placeholder="Which source's speech?" /></SelectTrigger>
            <SelectContent>{siblingSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {transcription.status === 'pending_integration' && (
          <p className="text-[10px] text-[#555E6E]">No speech-to-text provider is connected yet — this will produce real synchronized captions once one is.</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label className="text-xs text-[#8A93A6]">Animated Caption Style</label>
        <Select value={text.animated_caption_style || 'none'} onValueChange={(v) => onTextChange({ animated_caption_style: v })}>
          <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="caption-anim-style-select"><SelectValue /></SelectTrigger>
          <SelectContent>{CAPTION_ANIM_STYLES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <EditorCaptionsPanel
        captions={segments.map((s) => ({ id: s.id, start: s.start_seconds, end: s.end_seconds, text: s.text }))}
        duration={item.duration_seconds}
        currentTime={Math.max(0, localTime)}
        onAdd={(atTime) => onSegmentsChange([...segments, { id: newId(), start_seconds: atTime, end_seconds: Math.min(item.duration_seconds, atTime + 2), text: '', words: [] }].sort((a, b) => a.start_seconds - b.start_seconds))}
        onUpdate={(id, partial) => {
          const mapped = {};
          if (partial.start !== undefined) mapped.start_seconds = partial.start;
          if (partial.end !== undefined) mapped.end_seconds = partial.end;
          if (partial.text !== undefined) mapped.text = partial.text;
          onSegmentsChange(segments.map((s) => (s.id === id ? { ...s, ...mapped } : s)));
        }}
        onRemove={(id) => onSegmentsChange(segments.filter((s) => s.id !== id))}
      />

      {segments.length > 0 && (
        <div className="space-y-1.5 pt-1">
          {segments.map((seg) => (
            <div key={seg.id} className="flex items-center gap-1.5 flex-wrap" data-testid={`caption-segment-actions-${seg.id}`}>
              <Button size="sm" variant="outline" className="h-6 text-[9px] border-white/10" onClick={() => autoSplitWords(seg.id)} data-testid={`caption-autosplit-words-${seg.id}`}><Sparkles className="h-2.5 w-2.5 mr-1" />Split Words</Button>
              <Button size="sm" variant="outline" className="h-6 text-[9px] border-white/10" onClick={() => splitSegment(seg.id)} data-testid={`caption-split-segment-${seg.id}`}><Scissors className="h-2.5 w-2.5 mr-1" />Split</Button>
              <Button size="sm" variant="outline" className="h-6 text-[9px] border-white/10" onClick={() => mergeWithNext(seg.id)} data-testid={`caption-merge-segment-${seg.id}`}><Combine className="h-2.5 w-2.5 mr-1" />Merge Next</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
